import React, { useCallback } from "react";
import { fetchQuery, graphql, useFragment } from "react-relay";

import { faArrowAltCircleUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import cx from "classnames";

import { ReactComponent as GoogleDocsIcon } from "~/legacy-assets/images/logos/google_docs.svg";
import { promptGoogleAuth } from "~/lib/auth";
import { environment } from "~/models";
import { useDocsExportModal } from "~/stream/DocsExportModal";
import Menu from "~/ui/menu";
import { toasty as toast } from "~/ui/toast";

import type { ExportToDocs_DocsConnectedQuery as DocsConnectedQuery } from "~/__generated__/ExportToDocs_DocsConnectedQuery.graphql";
import type { ExportToDocs_stream$key } from "~/__generated__/ExportToDocs_stream.graphql";
import type { ExportToDocs_viewer$key } from "~/__generated__/ExportToDocs_viewer.graphql";

import styles from "./ExportToDocs.module.css";

const viewerFragment = graphql`
	fragment ExportToDocs_viewer on Viewer {
		uxSimplificationEnabled
		account {
			billingAccount {
				hasAccessToNotesIntegrationExport: hasAccessToFeature(feature: notes_integration_export)
			}
		}
	}
`;

const streamFragment = graphql`
	fragment ExportToDocs_stream on NoteStream {
		id
		displayName
	}
`;

export default function ExportToDocsMenuItem({
	viewer: viewerKey,
	stream: streamKey,
}: {
	viewer: ExportToDocs_viewer$key;
	stream: ExportToDocs_stream$key;
}) {
	const viewer = useFragment(viewerFragment, viewerKey);
	const hasAccessToNotesIntegrationExport = viewer.account?.billingAccount?.hasAccessToNotesIntegrationExport;
	const stream = useFragment(streamFragment, streamKey);

	const openDocsExportModal = useDocsExportModal(stream.id, stream.displayName);
	const exportToDocs = useCallback(async () => {
		const result = await fetchQuery<DocsConnectedQuery>(
			environment,
			graphql`
				query ExportToDocs_DocsConnectedQuery {
					viewer {
						googleDocsConnected
					}
				}
			`,
			{},
		).toPromise();

		if (result?.viewer.googleDocsConnected) {
			openDocsExportModal();
		} else {
			promptGoogleAuth({ docs: true })
				.then(openDocsExportModal)
				.catch(({ code, message: oauthMessage }) => {
					if (code == "access_denied") {
						toast.error(`Fellow requires access to Google Docs: (${oauthMessage})`);
					} else {
						toast.error(oauthMessage);
					}
				});
		}
	}, [openDocsExportModal]);

	return (
		<Menu.Item
			icon={hasAccessToNotesIntegrationExport ? undefined : faArrowAltCircleUp}
			onSelect={hasAccessToNotesIntegrationExport ? exportToDocs : undefined}
		>
			<GoogleDocsIcon className={cx("svg-inline--fa", "fa-fw", styles.customMenuItemIcon)} />
			{!viewer.uxSimplificationEnabled && "Export as "}Google Doc
		</Menu.Item>
	);
}
